import React, { useEffect, useState, createElement } from 'react';
import { MenuIcon, XIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
declare global {
  interface Window {
    Cal?: ((...args: unknown[]) => void) & {
      loaded?: boolean;
      ns?: Record<string, (...args: unknown[]) => void>;
      q?: unknown[];
    };
  }
}
export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.innerHTML = `
      (function (C, A, L) {
        let p = function (a, ar) { a.q.push(ar); };
        let d = C.document;
        C.Cal = C.Cal || function () {
          let cal = C.Cal; let ar = arguments;
          if (!cal.loaded) { cal.ns = {}; cal.q = cal.q || []; d.head.appendChild(d.createElement("script")).src = A; cal.loaded = true; }
          if (ar[0] === L) { const api = function () { p(api, arguments); }; const namespace = ar[1]; api.q = api.q || []; if(typeof namespace === "string"){cal.ns[namespace] = cal.ns[namespace] || api;p(cal.ns[namespace], ar);p(cal, ["initNamespace", namespace]);} else p(cal, ar); return;} p(cal, ar);
        };
      })(window, "https://app.cal.com/embed/embed.js", "init");
      Cal("init", "30min", {origin:"https://app.cal.com"});
      Cal.ns["30min"]("ui", {"hideEventTypeDetails":false,"layout":"month_view"});
    `;
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);
  const navLinks = [{
    label: 'Home',
    href: '/'
  }, {
    label: 'Work',
    href: '#work'
  }, {
    label: 'Services',
    href: '#services'
  }, {
    label: 'About',
    href: '#about'
  }];
  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith('#')) {
      if (window.location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          document.querySelector(href)?.scrollIntoView({
            behavior: 'smooth'
          });
        }, 100);
      } else {
        document.querySelector(href)?.scrollIntoView({
          behavior: 'smooth'
        });
      }
    } else {
      navigate(href);
    }
  };
  return <>
      {/* ── Desktop ── */}
      <div className={`navbar-outer ${scrolled ? 'scrolled' : ''}`} data-id="element-100">
        <nav className={`navbar-inner ${scrolled ? 'scrolled' : ''}`} data-id="element-101">
          {/* Logo */}
          <Link to="/" style={{
          textDecoration: 'none',
          flexShrink: 0
        }} data-id="element-102">
            <img src="https://cdn.magicpatterns.com/uploads/ihQUXxQBv8WxuR74FiSFNs/image.png" alt="GoLaunch" style={{
            height: scrolled ? 22 : 26,
            width: 'auto',
            transition: 'height 250ms ease-out'
          }} data-id="element-103" />
          </Link>

          {/* Center links */}
          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-104">
            {navLinks.map(link => <button key={link.label} onClick={() => handleNavClick(link.href)} className="text-sm bg-transparent border-none cursor-pointer rounded-full" style={{
            color: 'var(--muted)',
            padding: '6px 14px',
            transition: 'color 150ms ease, background 150ms ease',
            fontFamily: 'inherit',
            letterSpacing: '-0.01em'
          }} onMouseEnter={e => {
            e.currentTarget.style.color = 'var(--fg)';
            e.currentTarget.style.background = 'rgba(0,0,0,0.05)';
          }} onMouseLeave={e => {
            e.currentTarget.style.color = 'var(--muted)';
            e.currentTarget.style.background = 'transparent';
          }} data-id="element-105">
                {link.label}
              </button>)}
          </div>

          {/* CTA */}
          <button data-cal-link="go.launch/30min" data-cal-namespace="30min" data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}' className="text-sm font-medium rounded-full cursor-pointer" style={{
          background: 'var(--accent)',
          color: '#fff',
          border: 'none',
          padding: scrolled ? '7px 16px' : '9px 20px',
          letterSpacing: '-0.01em',
          transition: 'background 200ms ease, padding 250ms ease-out',
          fontFamily: 'inherit',
          flexShrink: 0
        }} onMouseEnter={e => e.currentTarget.style.background = '#A8421A'} onMouseLeave={e => e.currentTarget.style.background = 'var(--accent)'} data-id="element-106">
            Book a call
          </button>
        </nav>
      </div>

      {/* ── Mobile ── */}
      <header className="fixed top-0 left-0 right-0 z-50 md:hidden" style={{
      padding: '12px 16px'
    }} data-id="element-107">
        <div className="flex items-center justify-between" style={{
        background: scrolled ? 'rgba(255, 255, 255, 0.75)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(12px)' : 'none',
        border: `1px solid ${scrolled ? 'rgba(0,0,0,0.07)' : 'transparent'}`,
        borderRadius: 9999,
        padding: '10px 18px',
        boxShadow: scrolled ? '0 2px 16px rgba(0,0,0,0.06)' : 'none',
        transition: 'background 250ms ease-out, border-color 250ms ease-out, box-shadow 250ms ease-out'
      }} data-id="element-108">
          <Link to="/" style={{
          textDecoration: 'none'
        }} onClick={() => setMobileOpen(false)} data-id="element-109">
            <img src="https://cdn.magicpatterns.com/uploads/ihQUXxQBv8WxuR74FiSFNs/image.png" alt="GoLaunch" style={{
            height: 22,
            width: 'auto'
          }} data-id="element-110" />
          </Link>
          <button onClick={() => setMobileOpen(v => !v)} style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--fg)',
          padding: 4
        }} aria-label={mobileOpen ? 'Close menu' : 'Open menu'} data-id="element-111">
            {mobileOpen ? <XIcon size={20} data-id="element-112" /> : <MenuIcon size={20} data-id="element-113" />}
          </button>
        </div>

        {/* Mobile dropdown */}
        {mobileOpen && <div className="mt-2 flex flex-col animate-slide-down" style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(228, 228, 226, 0.7)',
        borderRadius: 20,
        padding: '6px 6px 12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.10)'
      }} data-id="element-114">
            {navLinks.map(link => <button key={link.label} onClick={() => handleNavClick(link.href)} className="w-full text-center text-base py-4 bg-transparent border-none cursor-pointer" style={{
          color: 'var(--fg)',
          borderRadius: 14,
          fontFamily: 'inherit',
          transition: 'background 150ms ease'
        }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'} data-id="element-115">
                {link.label}
              </button>)}
            <button data-cal-link="go.launch/30min" data-cal-namespace="30min" data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}' onClick={() => setMobileOpen(false)} className="w-full text-center text-sm font-medium py-3.5 rounded-full mt-1 cursor-pointer" style={{
          background: 'var(--accent)',
          color: '#fff',
          border: 'none',
          fontFamily: 'inherit'
        }} data-id="element-116">
              Book a call
            </button>
          </div>}
      </header>
    </>;
}